import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { formatTRY } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const ST = { aktif: ["Aktif", "text-emerald-700 border-emerald-300 bg-emerald-50"], iptal: ["İptal (ters kayıtla)", "text-rose-700 border-rose-300 bg-rose-50"], ters_kayit: ["Ters kayıt", "text-amber-700 border-amber-300 bg-amber-50"] };
const KIND = { customer: "Cari", supplier: "Tedarikçi", account: "Hesap" };
const partyLink = (p) => p.kind === "customer" ? `/cariler/${p.id}` : p.kind === "supplier" ? `/satin-alma?tab=tedarikciler&tedarikci=${p.id}` : "/finans";

function Party({ label, p, testid }) {
  return (
    <div className="border border-slate-300 rounded p-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">{label} · {KIND[p.kind]}</div>
      <Link to={partyLink(p)} className="font-semibold text-blue-700 hover:underline print:text-slate-900 print:no-underline" data-testid={testid}>{p.code} – {p.title}</Link>
    </div>
  );
}

// Virman Fişi – detay + yazdırma sayfası
export default function VirmanDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [v, setV] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => { setErr(""); api.get(`/virman/${id}`).then(({ data }) => setV(data)).catch((e) => setErr(apiError(e))); }, [id]);
  useEffect(() => { load(); }, [load]);
  const reverse = async () => {
    const note = window.prompt(`${v.no} ters kayıtla iptal edilsin mi? Açıklama:`, "");
    if (note === null) return;
    try { const { data } = await api.post(`/virman/${v.id}/reverse`, { note }); toast.success(`Ters kayıt ${data.no}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  if (err) return <div className="p-8 text-rose-700" data-testid="virman-detail-error">{err}</div>;
  if (!v) return <div className="p-8 text-slate-500" data-testid="virman-detail-loading">Virman fişi yükleniyor…</div>;
  const [stLabel, stCls] = ST[v.status] || ST.aktif;
  return (
    <div className="bg-white min-h-screen p-8 print:p-0 text-slate-900" data-testid="virman-detail-page">
      <div className="max-w-[800px] mx-auto">
        <div className="flex items-center gap-2 mb-4 print:hidden">
          <button className="h-9 px-3 rounded border border-slate-300 text-sm inline-flex items-center gap-2 hover:bg-slate-50" onClick={() => (window.history.length > 1 ? nav(-1) : nav("/virman"))} data-testid="virman-detail-back"><ArrowLeft className="h-4 w-4" />Geri</button>
          <div className="ml-auto flex gap-2">
            {can("finans", "virman") && v.status === "aktif" && <button className="h-9 px-4 rounded border border-rose-300 text-rose-700 text-sm inline-flex items-center gap-2 hover:bg-rose-50" onClick={reverse} data-testid="virman-detail-reverse"><Undo2 className="h-4 w-4" />Ters kayıt</button>}
            <button className="h-9 px-4 rounded bg-slate-900 text-white text-sm inline-flex items-center gap-2" onClick={() => window.print()} data-testid="virman-detail-print"><Printer className="h-4 w-4" />Yazdır</button>
          </div>
        </div>
        <div className="flex justify-between items-start border-b-2 border-slate-900 pb-3 mb-4">
          <div className="text-xl font-bold tracking-tight">İMAMOĞLU A.Ş</div>
          <div className="text-right">
            <div className="text-lg font-bold uppercase" data-testid="virman-detail-title">{v.kind === "cari" ? "Cari Virman Fişi" : "Hesap Virman Fişi"}</div>
            <div className="font-mono text-sm" data-testid="virman-detail-no">{v.no}</div>
            <div className="text-[11px] text-slate-500">Tarih {v.date}</div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mb-4 text-[13px]">
          <span className={`px-2 py-0.5 rounded border text-xs font-semibold ${stCls}`} data-testid="virman-detail-status">{stLabel}</span>
          {v.reversed_by && <span>Ters kaydı: <Link to={`/virman/${v.reversed_by}`} className="font-mono text-blue-700 hover:underline" data-testid="virman-detail-reversed-link">{v.reversed_no}</Link></span>}
          {v.reversal_of && <span>Asıl virman: <Link to={`/virman/${v.reversal_of}`} className="font-mono text-blue-700 hover:underline" data-testid="virman-detail-original-link">Asıl fişi aç</Link></span>}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Party label="Gönderen (ALACAK)" p={v.from} testid="virman-detail-from" />
          <Party label="Alan (BORÇ)" p={v.to} testid="virman-detail-to" />
        </div>
        <table className="w-full text-[13px] border-collapse mb-4">
          <tbody>
            <tr className="border-b border-slate-200"><td className="py-1.5 text-slate-500 w-48">Tutar</td><td className="font-mono" data-testid="virman-detail-amount">{v.amount.toLocaleString("tr-TR", { minimumFractionDigits: 2 })} {v.currency}</td></tr>
            {v.currency !== "TRY" && <tr className="border-b border-slate-200"><td className="py-1.5 text-slate-500">Kur</td><td className="font-mono">{v.fx_rate}</td></tr>}
            <tr className="border-b border-slate-200"><td className="py-1.5 text-slate-500">TL karşılığı</td><td className="font-mono font-semibold" data-testid="virman-detail-amount-try">{formatTRY(v.amount_try)}</td></tr>
            <tr className="border-b border-slate-200"><td className="py-1.5 text-slate-500">Açıklama</td><td data-testid="virman-detail-description">{v.description || "—"}</td></tr>
            <tr className="border-b border-slate-200"><td className="py-1.5 text-slate-500">Düzenleyen</td><td data-testid="virman-detail-user">{v.created_by_name}</td></tr>
            <tr className="border-b border-slate-200"><td className="py-1.5 text-slate-500">Kayıt zamanı</td><td className="font-mono text-xs">{new Date(v.created_at).toLocaleString("tr-TR")}</td></tr>
          </tbody>
        </table>
        <div className="flex justify-between mt-16 text-[13px]"><div className="border-t border-slate-800 w-2/5 pt-1 text-center">Düzenleyen</div><div className="border-t border-slate-800 w-2/5 pt-1 text-center">Onaylayan</div></div>
      </div>
    </div>
  );
}
